export const LEGAL_ENTITY = "Epicom Systems Ltd.";
export const PRODUCT = "Controlly";
export const CONTACT_EMAIL = "contact@getcontrolly.com";
export const JURISDICTION = "Province of Alberta, Canada";
export const EFFECTIVE_DATE = "July 16, 2026";

// Each doc: { slug, key, title, intro, sections: [{ heading, paragraphs?: [], bullets?: [] }] }
export const LEGAL_DOCS = {
  terms: {
    slug: "terms",
    key: "terms",
    title: "Terms of Service",
    intro: `These Terms of Service ("Terms") govern your access to and use of ${PRODUCT}, a remote desktop platform operated by ${LEGAL_ENTITY} ("we", "us", "our"). By creating an account, installing the ${PRODUCT} agent, or using the service, you agree to these Terms. If you do not agree, do not use ${PRODUCT}.`,
    sections: [
      { heading: "1. The Service", paragraphs: [
        `${PRODUCT} lets you register computers ("machines"), view their screens in real time, and remotely control them from a web browser using a lightweight agent you install on each machine.`,
      ] },
      { heading: "2. Eligibility & Accounts", paragraphs: [
        "You must be at least 18 years old and able to form a binding contract. You are responsible for all activity under your account and for keeping your login credentials secure. You sign in through Google; you are responsible for the security of that Google account.",
      ] },
      { heading: "3. Remote Access Consent (Important)", paragraphs: [
        "You may only install the agent on, and remotely access, computers that you own or are expressly authorized to control. By pairing a machine you represent and warrant that you have the legal right and all necessary consents to view and control that machine and any data on it.",
        "Using Controlly to access computers without authorization is strictly prohibited and may be a criminal offence. You are solely responsible for ensuring your use complies with all applicable laws and any workplace or third-party policies.",
      ] },
      { heading: "4. Subscriptions, Plans & Billing", paragraphs: [
        "Paid plans are billed in advance on a recurring basis (monthly) through our payment processor, Stripe. By subscribing you authorize us and Stripe to charge your payment method for the applicable fees until you cancel.",
        "All prices and charges are stated and processed in United States Dollars (USD), regardless of your location. Your bank or card issuer may apply currency-conversion or foreign-transaction fees, which are your responsibility.",
        "The Free plan is provided free of charge. Plan limits (machines, members, concurrent sessions) are enforced as described at checkout and may change with notice.",
      ] },
      { heading: "5. Lifetime Offer", paragraphs: [
        `From time to time we may offer a one-time "Lifetime" purchase for a specific plan. A Lifetime purchase grants access to that plan's features for as long as ${PRODUCT} remains operated by ${LEGAL_ENTITY}, with no recurring subscription fee. "Lifetime" refers to the lifetime of the product and service, not the purchaser, and does not guarantee the service will be offered indefinitely.`,
        "Lifetime purchases are subject to the Refund & Cancellation Policy (14-day window).",
      ] },
      { heading: "6. Acceptable Use", paragraphs: [
        "Your use of Controlly is subject to our Acceptable Use Policy, which is incorporated into these Terms by reference. We may suspend or terminate accounts that violate it.",
      ] },
      { heading: "7. Service Availability", paragraphs: [
        "We work to keep Controlly available and reliable but do not guarantee uninterrupted or error-free operation. The service is provided on an \"as is\" and \"as available\" basis. We may modify, suspend, or discontinue features at any time.",
      ] },
      { heading: "8. Disclaimers & Limitation of Liability", paragraphs: [
        "To the maximum extent permitted by law, we disclaim all warranties, express or implied. We are not liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of data, profits, or business, arising from your use of Controlly.",
        `Our total aggregate liability for any claim relating to the service will not exceed the greater of (a) the amount you paid to ${LEGAL_ENTITY} in the 12 months preceding the claim, or (b) CAD $100.`,
      ] },
      { heading: "9. Termination", paragraphs: [
        "You may stop using Controlly and cancel your subscription at any time. We may suspend or terminate your access for violation of these Terms or the Acceptable Use Policy. On termination, your right to use the service ends; provisions that by their nature should survive will survive.",
      ] },
      { heading: "10. Changes to These Terms", paragraphs: [
        "We may update these Terms from time to time. Material changes will be communicated by posting the updated Terms with a new effective date. Continued use after changes take effect constitutes acceptance.",
      ] },
      { heading: "11. Governing Law", paragraphs: [
        `These Terms are governed by the laws of the ${JURISDICTION} and the federal laws of Canada applicable therein, without regard to conflict-of-laws principles. You agree to the exclusive jurisdiction of the courts located in Alberta.`,
      ] },
      { heading: "12. Contact", paragraphs: [
        `Questions about these Terms can be sent to ${CONTACT_EMAIL}.`,
      ] },
    ],
  },

  privacy: {
    slug: "privacy",
    key: "privacy",
    title: "Privacy Policy",
    intro: `${LEGAL_ENTITY} ("we") respects your privacy. This Privacy Policy explains what personal information we collect through ${PRODUCT}, how we use and share it, and your rights. We handle personal information in accordance with Canada's Personal Information Protection and Electronic Documents Act (PIPEDA) and Alberta's Personal Information Protection Act (PIPA).`,
    sections: [
      { heading: "1. Information We Collect", bullets: [
        "Account information: your name, email address, and profile picture provided through Google sign-in.",
        "Machine information: names, operating system, hostname, IP address, and online status of computers you register.",
        "Session data: remote screen frames and mouse/keyboard input are relayed in real time to enable control. We do not intentionally record or store the contents of remote sessions.",
        "Billing information: subscription plan and payment records. Card details are processed and stored by Stripe, not by us.",
        "Usage & technical data: log data, timestamps, and limited diagnostics used to operate and secure the service.",
      ] },
      { heading: "2. How We Use Information", bullets: [
        "To provide, operate, and secure the Controlly service.",
        "To process payments and manage subscriptions.",
        "To send transactional emails (e.g., team invitations, PIN reset links, service notices).",
        "To prevent fraud, abuse, and unauthorized access.",
        "To comply with legal obligations.",
      ] },
      { heading: "3. Service Providers (Sub-processors)", paragraphs: [
        "We share personal information only with service providers that help us run Controlly, under appropriate safeguards:",
      ], bullets: [
        "Google — authentication (sign-in).",
        "Stripe — payment processing and billing.",
        "Resend — transactional email delivery.",
        "Cloud hosting & database providers — to host the application and store account data.",
      ] },
      { heading: "4. Remote Session Data", paragraphs: [
        "Screen frames and control inputs are relayed between the agent and your browser to make remote control work. This data passes through our servers transiently to bridge the connection and is not intentionally persisted. You are responsible for the data visible on machines you control.",
      ] },
      { heading: "5. Data Retention", paragraphs: [
        "We retain account and billing information for as long as your account is active and as needed to comply with legal, tax, and accounting requirements. Deleted accounts are archived and recoverable for 30 days, after which they are permanently purged. You may request deletion of your account at any time.",
      ] },
      { heading: "6. Security", paragraphs: [
        "We use industry-standard measures including encrypted connections, one-time pairing tokens with a limited install window, device-key revocation, and an on-screen indicator shown on a machine while it is being viewed. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
      ] },
      { heading: "7. Your Rights", paragraphs: [
        `Subject to applicable law, you may request access to, correction of, or deletion of your personal information, and you may withdraw consent. To exercise these rights, contact ${CONTACT_EMAIL}. You also have the right to complain to the Office of the Privacy Commissioner of Canada or the Office of the Information and Privacy Commissioner of Alberta.`,
      ] },
      { heading: "8. International Transfers", paragraphs: [
        "Your information may be processed and stored by our service providers in countries outside Canada. Where this occurs, we take steps to ensure comparable protection consistent with PIPEDA and PIPA.",
      ] },
      { heading: "9. Children", paragraphs: [
        "Controlly is not directed to individuals under 18 and we do not knowingly collect their personal information.",
      ] },
      { heading: "10. Changes & Contact", paragraphs: [
        `We may update this Privacy Policy and will post changes with a new effective date. Privacy questions and requests can be sent to ${CONTACT_EMAIL}.`,
      ] },
    ],
  },

  "refund-policy": {
    slug: "refund-policy",
    key: "refund-policy",
    title: "Refund & Cancellation Policy",
    intro: `This policy explains cancellations and refunds for ${PRODUCT}, operated by ${LEGAL_ENTITY}.`,
    sections: [
      { heading: "1. Monthly Subscriptions", paragraphs: [
        "You can cancel your subscription at any time from the Billing page. When you cancel, your plan remains active until the end of the current billing period, after which it will not renew. We do not provide prorated refunds for partial billing periods unless required by law.",
        "All charges and any refunds are processed in United States Dollars (USD). Where a refund is issued, we refund the original USD amount charged; we are not responsible for currency-conversion differences or foreign-transaction fees applied by your bank or card issuer.",
      ] },
      { heading: "2. Lifetime Offer — 14-Day Refund Window", paragraphs: [
        "One-time \"Lifetime\" purchases may be refunded if you request a refund within 14 days of the purchase date. After 14 days, Lifetime purchases are final and non-refundable.",
        `To request a refund within the window, email ${CONTACT_EMAIL} from the account's email address with your purchase details. Approved refunds are issued to the original payment method via Stripe and may take several business days to appear.`,
      ] },
      { heading: "3. Free Plan", paragraphs: [
        "The Free plan involves no charges or refunds.",
      ] },
      { heading: "4. Chargebacks", paragraphs: [
        "If you believe a charge is in error, please contact us first so we can help. Initiating a chargeback without contacting us may result in suspension of your account.",
      ] },
      { heading: "5. Contact", paragraphs: [
        `All billing questions and refund requests: ${CONTACT_EMAIL}.`,
      ] },
    ],
  },

  "acceptable-use": {
    slug: "acceptable-use",
    key: "acceptable-use",
    title: "Acceptable Use Policy",
    intro: `This Acceptable Use Policy applies to everyone who uses ${PRODUCT}. It exists to keep the service safe and lawful. Violations may result in suspension or termination.`,
    sections: [
      { heading: "1. Authorized Access Only", paragraphs: [
        "You may only install the agent on, and control, computers you own or are explicitly authorized to access. You must not use Controlly to gain unauthorized access to any device or data.",
      ] },
      { heading: "2. Prohibited Conduct", bullets: [
        "No unauthorized, deceptive, or fraudulent remote access (including tech-support scams or impersonation).",
        "No use to access, distribute, or store unlawful content or malware.",
        "No attempts to disable, bypass, or hide the on-screen session indicator or other security features.",
        "No interference with, overloading, or probing of the service or its infrastructure.",
        "No reselling or providing the service to third parties except as expressly permitted.",
        "No violation of any applicable law, regulation, or third-party rights.",
      ] },
      { heading: "3. Anti-Scam Commitment", paragraphs: [
        "Controlly displays a persistent, on-screen indicator on any machine while it is being viewed or controlled. Attempting to trick, coerce, or deceive a person into granting remote access is a serious violation and may be reported to authorities.",
      ] },
      { heading: "4. Enforcement", paragraphs: [
        `We may investigate suspected violations and may suspend or terminate access, remove content, and cooperate with law enforcement. Report abuse to ${CONTACT_EMAIL}.`,
      ] },
    ],
  },

  cookies: {
    slug: "cookies",
    key: "cookies",
    title: "Cookie Notice",
    intro: `This Cookie Notice explains how ${PRODUCT} uses cookies and similar technologies.`,
    sections: [
      { heading: "1. What We Use", bullets: [
        "Essential session cookie: a secure, HTTP-only cookie that keeps you signed in. The service will not work without it.",
        "Local storage: we store limited preferences (such as your currently selected workspace) in your browser.",
      ] },
      { heading: "2. What We Don't Use", paragraphs: [
        "We do not use third-party advertising or cross-site tracking cookies on the application.",
      ] },
      { heading: "3. Managing Cookies", paragraphs: [
        "You can clear cookies and local storage through your browser settings, but doing so will sign you out and may reset preferences. Blocking the essential session cookie will prevent you from using the service.",
      ] },
      { heading: "4. Contact", paragraphs: [
        `Questions about this notice: ${CONTACT_EMAIL}.`,
      ] },
    ],
  },
};

export const LEGAL_LINKS = [
  { to: "/terms", label: "Terms of Service" },
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/refund-policy", label: "Refund Policy" },
  { to: "/acceptable-use", label: "Acceptable Use" },
  { to: "/cookies", label: "Cookies" },
];
